import { type ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { useAppSelector } from 'dux/app/hooks';

import { Box, Hidden, Text } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import { mediaMinWidth } from '@prose-ui/utils/media';
import Image from 'next/image';

import Spacer from 'Components/Spacer';

import bgPictureCleanser from 'assets/images/productPages/prose-custom-cleanser.jpg';
import bgPictureMoisturizer from 'assets/images/productPages/prose-custom-moisturizer.jpg';
import bgPictureSerum from 'assets/images/productPages/prose-custom-serum.jpg';

import { getUserFirstName } from 'dux/user/selectors';

const ProductImage = styled(Image)`
  height: auto;
  width: 100%;
  border-radius: 10px;

  &.is-active {
    border: 2px solid black;
  }
`;

const PromoCTA = styled.div`
  width: 100%;
  ${mediaMinWidth('sm')} {
    order: -1;
    width: auto;
  }
`;

export const ThreeStepCheckoutSuccessPromo = ({ cta }: { cta: ReactNode }) => {
  const userName = useAppSelector(getUserFirstName);
  const [activeIndex, setActiveIndex] = useState(0);
  const products = [
    { id: 1, name: 'Cleanser', src: bgPictureCleanser },
    { id: 2, name: 'Serum', src: bgPictureSerum },
    { id: 3, name: 'Moisturizer', src: bgPictureMoisturizer },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % products.length);
    }, 1500);

    return () => clearInterval(interval);
  }, [products.length]);

  return (
    <>
      <Hidden below="desktop">
        <Spacer size={32} />
      </Hidden>
      <Box
        __margin="0 auto"
        display="flex"
        flexDirection="column"
        gap={{ mobile: '6x', tablet: '4x' }}
        padding="4x"
      >
        <Box display="flex" flexDirection="column" gap={{ mobile: '2x', tablet: '4x' }}>
          <Hidden below="tablet">
            <Text align="center" data-testid="checkout-success-promo-title" variant="heading4">
              A powerful + personalized
              <br /> 3-step skincare system for:
            </Text>
          </Hidden>
          <Hidden above="mobile">
            <Text textAlign="center" variant="heading6">
              A powerful + personalized
              <br /> 3-step skincare system for:
            </Text>
          </Hidden>
        </Box>
        <Text textAlign="center" variant="labelLg">
          {userName}
        </Text>
        <Box
          alignItems="center"
          display="flex"
          flexDirection="column"
          gap={{ mobile: '6x', tablet: '14x' }}
        >
          <Box __width="100%" display="flex" gap="2x" justifyContent="center">
            {products.map((product, index) => (
              <Box
                key={product.id}
                className={activeIndex === index ? 'active-product' : ''}
                display="flex"
                flex={1}
                flexDirection="column"
                gap="3x"
              >
                <ProductImage
                  alt={product.name}
                  src={product.src}
                  style={{
                    border: activeIndex === index ? '2px solid #000' : 'none',
                    transition: 'border 0.3s ease-in-out',
                  }}
                />
                <Hidden below="tablet">
                  <Text
                    __whiteSpace="noWrap"
                    style={{
                      fontWeight: activeIndex === index ? 500 : 400,
                    }}
                    textAlign="center"
                    variant="bodyMd"
                  >
                    {`${product.id}. ${product.name}`}
                  </Text>
                </Hidden>
                <Hidden above="mobile">
                  <Text
                    __whiteSpace="noWrap"
                    style={{
                      fontWeight: activeIndex === index ? 500 : 400,
                    }}
                    textAlign="center"
                    variant="bodySm"
                  >
                    {`${product.id}. ${product.name}`}
                  </Text>
                </Hidden>
              </Box>
            ))}
          </Box>
          <PromoCTA>{cta}</PromoCTA>
        </Box>
      </Box>
    </>
  );
};
